import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent } from '../components/ui/card';
import { Cookie, Mail, MapPin, Phone, Check, X } from 'lucide-react';
import { SEO } from '../components/SEO';

const content = {
  en: {
    title: "Cookie Policy",
    lastUpdated: "Last Updated: April 2026",
    intro: {
      title: "Introduction",
      text: "WeDo HR Services UG (\"WeDo\", \"we\", \"us\", or \"our\") uses cookies and similar technologies on our website (www.wedohr.net). This Cookie Policy explains what cookies are, the types of cookies we use, why we use them, and how you can manage your cookie preferences. This policy should be read together with our Privacy Policy."
    },
    whatAre: {
      title: "What Are Cookies?",
      text: "Cookies are small text files that are stored on your device (computer, tablet, or mobile phone) when you visit a website. They are widely used to make websites work more efficiently, provide a better user experience, and give website owners useful information about how their site is being used.",
      session: "Session cookies are temporary and are deleted when you close your browser.",
      persistent: "Persistent cookies remain on your device for a set period or until you delete them."
    },
    types: {
      title: "Types of Cookies We Use",
      intro: "We use the following categories of cookies on our website:",
      categories: [
        {
          name: "Strictly Necessary Cookies",
          description: "These cookies are essential for the website to function properly. They enable basic functions like page navigation, secure area access, and form submissions. The website cannot function properly without these cookies.",
          required: true,
          examples: [
            "Session management",
            "Authentication cookies",
            "Security cookies",
            "Language preference storage"
          ]
        },
        {
          name: "Functional Cookies",
          description: "These cookies enable enhanced functionality and personalization. They may be set by us or by third-party providers whose services we have added to our pages. If you do not allow these cookies, some or all of these features may not function properly.",
          required: false,
          examples: [
            "Language and region preferences",
            "User interface customization",
            "Form data remembering",
            "Chat and support features"
          ]
        },
        {
          name: "Analytics Cookies",
          description: "These cookies help us understand how visitors interact with our website by collecting and reporting information anonymously. This helps us improve our website's performance and user experience.",
          required: false,
          examples: [
            "Page visit statistics",
            "Traffic sources",
            "User journey analysis",
            "Performance monitoring"
          ]
        },
        {
          name: "Marketing Cookies",
          description: "These cookies may be set through our site by our advertising partners. They may be used to build a profile of your interests and show you relevant advertisements on other sites. They do not directly store personal information but are based on uniquely identifying your browser and device.",
          required: false,
          examples: [
            "Targeted advertising",
            "Social media integration",
            "Campaign effectiveness tracking",
            "Remarketing purposes"
          ]
        }
      ]
    },
    specific: {
      title: "Specific Cookies We Use",
      intro: "Below is a list of the main cookies we use:",
      cookies: [
        {
          name: "Session Cookie",
          purpose: "Maintains your session while browsing our website",
          duration: "Session",
          type: "Necessary"
        },
        {
          name: "Language Preference",
          purpose: "Remembers your language selection (English/Arabic)",
          duration: "1 year",
          type: "Functional"
        },
        {
          name: "Authentication",
          purpose: "Keeps you logged in to your account",
          duration: "Session / 30 days",
          type: "Necessary"
        },
        {
          name: "Analytics (if enabled)",
          purpose: "Collects anonymous usage statistics",
          duration: "Up to 2 years",
          type: "Analytics"
        }
      ]
    },
    thirdParty: {
      title: "Third-Party Cookies",
      text: "Some cookies on our website are set by third-party services. These may include:",
      items: [
        "Analytics services (e.g., Google Analytics) for website performance analysis",
        "Social media platforms for sharing features",
        "Security services for fraud prevention",
        "Embedded content providers (e.g., videos, maps)"
      ],
      note: "These third parties have their own cookie policies, and we encourage you to review them. We do not have control over third-party cookies."
    },
    manage: {
      title: "Managing Your Cookie Preferences",
      intro: "You have several options to manage cookies:",
      options: [
        {
          title: "Browser Settings",
          description: "Most web browsers allow you to control cookies through their settings. You can typically find these in the 'Options' or 'Preferences' menu. You can set your browser to block or delete cookies, or to notify you when a cookie is being set."
        },
        {
          title: "Opt-Out Links",
          description: "Some analytics and advertising services offer direct opt-out mechanisms. For example, Google Analytics provides an opt-out browser add-on."
        },
        {
          title: "Device Settings",
          description: "Your mobile device may have settings that enable you to control the collection of data for interest-based advertising."
        }
      ],
      warning: "Please note that if you disable certain cookies, some features of our website may not work as intended, and your user experience may be affected."
    },
    browsers: {
      title: "Browser-Specific Instructions",
      intro: "Here are links to cookie management instructions for popular browsers:",
      items: [
        { name: "Google Chrome", url: "https://support.google.com/chrome/answer/95647" },
        { name: "Mozilla Firefox", url: "https://support.mozilla.org/en-US/kb/cookies-information-websites-store-on-your-computer" },
        { name: "Safari", url: "https://support.apple.com/guide/safari/manage-cookies-sfri11471/mac" },
        { name: "Microsoft Edge", url: "https://support.microsoft.com/en-us/microsoft-edge/delete-cookies-in-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09" },
        { name: "Opera", url: "https://help.opera.com/en/latest/web-preferences/#cookies" }
      ]
    },
    dnt: {
      title: "Do Not Track",
      text: "Some browsers offer a 'Do Not Track' (DNT) feature that signals to websites that you do not wish to be tracked. Our website currently does not respond to DNT signals, but you can use the cookie management options described above to control tracking."
    },
    changes: {
      title: "Changes to This Policy",
      text: "We may update this Cookie Policy from time to time to reflect changes in our practices or for other operational, legal, or regulatory reasons. We encourage you to review this policy periodically. The date at the top of this page indicates when this policy was last updated."
    },
    contact: {
      title: "Contact Us",
      intro: "If you have any questions about our use of cookies or this Cookie Policy, please contact us:",
      company: "WeDo HR Services UG",
      address: "Düsseldorf, Germany",
      email: "info@wedohr.net",
      phone: "+49 1590 6453419"
    }
  },
  ar: {
    title: "سياسة ملفات تعريف الارتباط",
    lastUpdated: "آخر تحديث: أبريل 2026",
    intro: {
      title: "مقدمة",
      text: "تستخدم شركة ويدو لخدمات الموارد البشرية (\"ويدو\"، \"نحن\"، \"لنا\") ملفات تعريف الارتباط والتقنيات المماثلة على موقعنا الإلكتروني (www.wedohr.net). توضح سياسة ملفات تعريف الارتباط هذه ما هي ملفات تعريف الارتباط، وأنواع الملفات التي نستخدمها، ولماذا نستخدمها، وكيف يمكنك إدارة تفضيلاتك. يجب قراءة هذه السياسة مع سياسة الخصوصية الخاصة بنا."
    },
    whatAre: {
      title: "ما هي ملفات تعريف الارتباط؟",
      text: "ملفات تعريف الارتباط هي ملفات نصية صغيرة يتم تخزينها على جهازك (الكمبيوتر أو الجهاز اللوحي أو الهاتف المحمول) عند زيارة موقع إلكتروني. تُستخدم على نطاق واسع لجعل المواقع تعمل بكفاءة أكبر، وتوفير تجربة مستخدم أفضل، وإعطاء مالكي المواقع معلومات مفيدة حول كيفية استخدام موقعهم.",
      session: "ملفات تعريف الارتباط الخاصة بالجلسة مؤقتة ويتم حذفها عند إغلاق المتصفح.",
      persistent: "تبقى ملفات تعريف الارتباط الدائمة على جهازك لفترة محددة أو حتى تحذفها."
    },
    types: {
      title: "أنواع ملفات تعريف الارتباط التي نستخدمها",
      intro: "نستخدم الفئات التالية من ملفات تعريف الارتباط على موقعنا:",
      categories: [
        {
          name: "ملفات تعريف الارتباط الضرورية",
          description: "هذه الملفات ضرورية لعمل الموقع بشكل صحيح. تمكّن الوظائف الأساسية مثل التنقل بين الصفحات والوصول إلى المناطق الآمنة وإرسال النماذج. لا يمكن للموقع العمل بشكل صحيح بدون هذه الملفات.",
          required: true,
          examples: [
            "إدارة الجلسة",
            "ملفات المصادقة",
            "ملفات الأمان",
            "تخزين تفضيل اللغة"
          ]
        },
        {
          name: "ملفات تعريف الارتباط الوظيفية",
          description: "تتيح هذه الملفات وظائف محسنة وتخصيصاً. قد نقوم نحن أو مزودو خدمات الطرف الثالث الذين أضفنا خدماتهم إلى صفحاتنا بتعيينها. إذا لم تسمح بهذه الملفات، فقد لا تعمل بعض أو كل هذه الميزات بشكل صحيح.",
          required: false,
          examples: [
            "تفضيلات اللغة والمنطقة",
            "تخصيص واجهة المستخدم",
            "تذكر بيانات النموذج",
            "ميزات الدردشة والدعم"
          ]
        },
        {
          name: "ملفات تعريف الارتباط التحليلية",
          description: "تساعدنا هذه الملفات على فهم كيفية تفاعل الزوار مع موقعنا من خلال جمع المعلومات والإبلاغ عنها بشكل مجهول. يساعدنا هذا في تحسين أداء موقعنا وتجربة المستخدم.",
          required: false,
          examples: [
            "إحصائيات زيارة الصفحات",
            "مصادر الزيارات",
            "تحليل رحلة المستخدم",
            "مراقبة الأداء"
          ]
        },
        {
          name: "ملفات تعريف الارتباط التسويقية",
          description: "قد يتم تعيين هذه الملفات من خلال موقعنا بواسطة شركائنا الإعلانيين. يمكن استخدامها لبناء ملف تعريف لاهتماماتك وعرض إعلانات ذات صلة على مواقع أخرى. لا تخزن المعلومات الشخصية مباشرة ولكنها تعتمد على التعرف الفريد على متصفحك وجهازك.",
          required: false,
          examples: [
            "الإعلانات المستهدفة",
            "تكامل وسائل التواصل الاجتماعي",
            "تتبع فعالية الحملات",
            "أغراض إعادة التسويق"
          ]
        }
      ]
    },
    specific: {
      title: "ملفات تعريف الارتباط المحددة التي نستخدمها",
      intro: "فيما يلي قائمة بملفات تعريف الارتباط الرئيسية التي نستخدمها:",
      cookies: [
        {
          name: "ملف الجلسة",
          purpose: "يحافظ على جلستك أثناء تصفح موقعنا",
          duration: "الجلسة",
          type: "ضروري"
        },
        {
          name: "تفضيل اللغة",
          purpose: "يتذكر اختيارك للغة (الإنجليزية/العربية)",
          duration: "سنة واحدة",
          type: "وظيفي"
        },
        {
          name: "المصادقة",
          purpose: "يبقيك مسجلاً في حسابك",
          duration: "الجلسة / 30 يوماً",
          type: "ضروري"
        },
        {
          name: "التحليلات (إذا كانت مفعلة)",
          purpose: "يجمع إحصائيات استخدام مجهولة",
          duration: "حتى سنتين",
          type: "تحليلي"
        }
      ]
    },
    thirdParty: {
      title: "ملفات تعريف ارتباط الطرف الثالث",
      text: "يتم تعيين بعض ملفات تعريف الارتباط على موقعنا بواسطة خدمات الطرف الثالث. قد تشمل هذه:",
      items: [
        "خدمات التحليلات (مثل Google Analytics) لتحليل أداء الموقع",
        "منصات التواصل الاجتماعي لميزات المشاركة",
        "خدمات الأمان لمنع الاحتيال",
        "مزودو المحتوى المضمن (مثل الفيديوهات والخرائط)"
      ],
      note: "لدى هذه الأطراف الثالثة سياسات ملفات تعريف الارتباط الخاصة بها، ونشجعك على مراجعتها. ليس لدينا سيطرة على ملفات تعريف ارتباط الطرف الثالث."
    },
    manage: {
      title: "إدارة تفضيلات ملفات تعريف الارتباط",
      intro: "لديك عدة خيارات لإدارة ملفات تعريف الارتباط:",
      options: [
        {
          title: "إعدادات المتصفح",
          description: "تتيح لك معظم متصفحات الويب التحكم في ملفات تعريف الارتباط من خلال إعداداتها. يمكنك عادةً العثور عليها في قائمة 'خيارات' أو 'تفضيلات'. يمكنك ضبط متصفحك لحظر أو حذف ملفات تعريف الارتباط، أو إخطارك عند تعيين ملف."
        },
        {
          title: "روابط إلغاء الاشتراك",
          description: "تقدم بعض خدمات التحليلات والإعلانات آليات إلغاء اشتراك مباشرة. على سبيل المثال، يوفر Google Analytics إضافة متصفح لإلغاء الاشتراك."
        },
        {
          title: "إعدادات الجهاز",
          description: "قد يحتوي جهازك المحمول على إعدادات تمكنك من التحكم في جمع البيانات للإعلانات القائمة على الاهتمامات."
        }
      ],
      warning: "يرجى ملاحظة أنه إذا قمت بتعطيل ملفات تعريف ارتباط معينة، فقد لا تعمل بعض ميزات موقعنا كما هو مقصود، وقد تتأثر تجربة المستخدم الخاصة بك."
    },
    browsers: {
      title: "تعليمات خاصة بالمتصفح",
      intro: "فيما يلي روابط لتعليمات إدارة ملفات تعريف الارتباط للمتصفحات الشائعة:",
      items: [
        { name: "Google Chrome", url: "https://support.google.com/chrome/answer/95647" },
        { name: "Mozilla Firefox", url: "https://support.mozilla.org/en-US/kb/cookies-information-websites-store-on-your-computer" },
        { name: "Safari", url: "https://support.apple.com/guide/safari/manage-cookies-sfri11471/mac" },
        { name: "Microsoft Edge", url: "https://support.microsoft.com/en-us/microsoft-edge/delete-cookies-in-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09" },
        { name: "Opera", url: "https://help.opera.com/en/latest/web-preferences/#cookies" }
      ]
    },
    dnt: {
      title: "عدم التتبع",
      text: "تقدم بعض المتصفحات ميزة 'عدم التتبع' (DNT) التي ترسل إشارة للمواقع بأنك لا ترغب في التتبع. موقعنا حالياً لا يستجيب لإشارات DNT، ولكن يمكنك استخدام خيارات إدارة ملفات تعريف الارتباط الموضحة أعلاه للتحكم في التتبع."
    },
    changes: {
      title: "التغييرات على هذه السياسة",
      text: "قد نقوم بتحديث سياسة ملفات تعريف الارتباط هذه من وقت لآخر لتعكس التغييرات في ممارساتنا أو لأسباب تشغيلية أو قانونية أو تنظيمية أخرى. نشجعك على مراجعة هذه السياسة بشكل دوري. يشير التاريخ في أعلى هذه الصفحة إلى آخر تحديث لهذه السياسة."
    },
    contact: {
      title: "اتصل بنا",
      intro: "إذا كانت لديك أي أسئلة حول استخدامنا لملفات تعريف الارتباط أو هذه السياسة، يرجى الاتصال بنا:",
      company: "ويدو لخدمات الموارد البشرية",
      address: "دوسلدورف، ألمانيا",
      email: "info@wedohr.net",
      phone: "+49 1590 6453419"
    }
  }
};

export const CookiePolicyPage = () => {
  const { language, direction } = useLanguage();
  const t = content[language] || content.en;

  return (
    <div className="min-h-screen bg-[#F9FAFB]" dir={direction} data-testid="cookie-policy-page">
      <SEO
        title="Cookie Policy"
        titleAr="سياسة ملفات تعريف الارتباط"
        description="WeDo HR Services cookie policy. Learn about the types of cookies we use, why we use them, and how to manage your cookie preferences on our website."
        descriptionAr="سياسة ملفات تعريف الارتباط لخدمات ويدو للموارد البشرية. تعرف على أنواع ملفات تعريف الارتباط التي نستخدمها ولماذا نستخدمها وكيفية إدارة تفضيلاتك."
        canonicalUrl="/cookies"
        language={language}
      />
      {/* Header */}
      <section className="bg-white border-b border-[#E5E7EB] py-16">
        <div className="container-custom">
          <div className="flex items-center gap-4 mb-4">
            <div className="h-14 w-14 rounded-xl bg-[#33AADD]/10 flex items-center justify-center">
              <Cookie className="h-7 w-7 text-[#33AADD]" />
            </div>
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold text-[#111827]">{t.title}</h1>
              <p className="text-sm text-[#6B7280] mt-1">{t.lastUpdated}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="section-padding">
        <div className="container-custom max-w-4xl">
          <div className="space-y-8">
            {/* Introduction */}
            <Card>
              <CardContent className="p-6">
                <h2 className="text-xl font-semibold text-[#111827] mb-3">{t.intro.title}</h2>
                <p className="text-[#6B7280] leading-relaxed">{t.intro.text}</p>
              </CardContent>
            </Card>

            {/* What Are Cookies */}
            <Card>
              <CardContent className="p-6">
                <h2 className="text-xl font-semibold text-[#111827] mb-3">{t.whatAre.title}</h2>
                <p className="text-[#6B7280] leading-relaxed mb-4">{t.whatAre.text}</p>
                <div className="space-y-2 text-sm text-[#6B7280]">
                  <p><strong>{language === 'ar' ? 'ملفات الجلسة:' : 'Session Cookies:'}</strong> {t.whatAre.session}</p>
                  <p><strong>{language === 'ar' ? 'الملفات الدائمة:' : 'Persistent Cookies:'}</strong> {t.whatAre.persistent}</p>
                </div>
              </CardContent>
            </Card>

            {/* Types of Cookies */}
            <Card>
              <CardContent className="p-6">
                <h2 className="text-xl font-semibold text-[#111827] mb-3">{t.types.title}</h2>
                <p className="text-[#6B7280] mb-4">{t.types.intro}</p>
                <div className="space-y-6">
                  {t.types.categories.map((category, index) => (
                    <div key={index} className="border rounded-lg overflow-hidden">
                      <div className={`p-4 ${category.required ? 'bg-[#33AADD]/5 border-b border-[#33AADD]/20' : 'bg-[#F9FAFB] border-b'}`}>
                        <div className="flex items-center justify-between">
                          <h3 className="font-semibold text-[#111827]">{category.name}</h3>
                          <span className={`text-xs px-2 py-1 rounded-full ${category.required ? 'bg-[#33AADD]/10 text-[#33AADD]' : 'bg-gray-100 text-gray-600'}`}>
                            {category.required 
                              ? (language === 'ar' ? 'مطلوب' : 'Required') 
                              : (language === 'ar' ? 'اختياري' : 'Optional')}
                          </span>
                        </div>
                        <p className="text-sm text-[#6B7280] mt-2">{category.description}</p>
                      </div>
                      <div className="p-4">
                        <p className="text-xs text-[#6B7280] mb-2 font-medium">
                          {language === 'ar' ? 'أمثلة:' : 'Examples:'}
                        </p>
                        <ul className="grid grid-cols-2 gap-2">
                          {category.examples.map((example, i) => (
                            <li key={i} className="flex items-center gap-2 text-sm text-[#6B7280]">
                              <div className="h-1.5 w-1.5 rounded-full bg-[#33AADD]" />
                              {example}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Specific Cookies */}
            <Card>
              <CardContent className="p-6">
                <h2 className="text-xl font-semibold text-[#111827] mb-3">{t.specific.title}</h2>
                <p className="text-[#6B7280] mb-4">{t.specific.intro}</p>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-[#E5E7EB]">
                        <th className="text-start p-3 text-[#111827] font-semibold">
                          {language === 'ar' ? 'الاسم' : 'Name'}
                        </th>
                        <th className="text-start p-3 text-[#111827] font-semibold">
                          {language === 'ar' ? 'الغرض' : 'Purpose'}
                        </th>
                        <th className="text-start p-3 text-[#111827] font-semibold">
                          {language === 'ar' ? 'المدة' : 'Duration'}
                        </th>
                        <th className="text-start p-3 text-[#111827] font-semibold">
                          {language === 'ar' ? 'النوع' : 'Type'}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {t.specific.cookies.map((cookie, index) => (
                        <tr key={index} className="border-b border-[#E5E7EB]">
                          <td className="p-3 text-[#111827] font-medium">{cookie.name}</td>
                          <td className="p-3 text-[#6B7280]">{cookie.purpose}</td>
                          <td className="p-3 text-[#6B7280]">{cookie.duration}</td>
                          <td className="p-3">
                            <span className="text-xs px-2 py-1 rounded-full bg-[#33AADD]/10 text-[#33AADD]">
                              {cookie.type}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            {/* Third-Party Cookies */}
            <Card>
              <CardContent className="p-6">
                <h2 className="text-xl font-semibold text-[#111827] mb-3">{t.thirdParty.title}</h2>
                <p className="text-[#6B7280] mb-4">{t.thirdParty.text}</p>
                <ul className="list-disc list-inside space-y-2 text-[#6B7280] mb-4">
                  {t.thirdParty.items.map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
                <p className="text-sm text-[#6B7280] italic">{t.thirdParty.note}</p>
              </CardContent>
            </Card>

            {/* Managing Cookies */}
            <Card>
              <CardContent className="p-6">
                <h2 className="text-xl font-semibold text-[#111827] mb-3">{t.manage.title}</h2>
                <p className="text-[#6B7280] mb-4">{t.manage.intro}</p>
                <div className="space-y-4 mb-4">
                  {t.manage.options.map((option, index) => (
                    <div key={index} className="bg-[#F9FAFB] rounded-lg p-4">
                      <h3 className="font-medium text-[#111827] mb-1">{option.title}</h3>
                      <p className="text-sm text-[#6B7280]">{option.description}</p>
                    </div>
                  ))}
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <p className="text-sm text-amber-800">{t.manage.warning}</p>
                </div>
              </CardContent>
            </Card>

            {/* Browser Instructions */}
            <Card>
              <CardContent className="p-6">
                <h2 className="text-xl font-semibold text-[#111827] mb-3">{t.browsers.title}</h2>
                <p className="text-[#6B7280] mb-4">{t.browsers.intro}</p>
                <ul className="space-y-2">
                  {t.browsers.items.map((browser, index) => (
                    <li key={index}>
                      <a 
                        href={browser.url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-[#33AADD] hover:underline"
                      >
                        {browser.name}
                      </a>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Do Not Track */}
            <Card>
              <CardContent className="p-6">
                <h2 className="text-xl font-semibold text-[#111827] mb-3">{t.dnt.title}</h2>
                <p className="text-[#6B7280] leading-relaxed">{t.dnt.text}</p>
              </CardContent>
            </Card>

            {/* Changes */}
            <Card>
              <CardContent className="p-6">
                <h2 className="text-xl font-semibold text-[#111827] mb-3">{t.changes.title}</h2>
                <p className="text-[#6B7280] leading-relaxed">{t.changes.text}</p>
              </CardContent>
            </Card>

            {/* Contact */}
            <Card>
              <CardContent className="p-6">
                <h2 className="text-xl font-semibold text-[#111827] mb-3">{t.contact.title}</h2>
                <p className="text-[#6B7280] mb-4">{t.contact.intro}</p>
                <div className="bg-[#F9FAFB] rounded-lg p-4 space-y-2">
                  <p className="font-medium text-[#111827]">{t.contact.company}</p>
                  <div className="flex items-center gap-2 text-[#6B7280]">
                    <MapPin className="h-4 w-4" />
                    <span>{t.contact.address}</span>
                  </div>
                  <div className="flex items-center gap-2 text-[#6B7280]">
                    <Mail className="h-4 w-4" />
                    <a href={`mailto:${t.contact.email}`} className="text-[#33AADD] hover:underline">{t.contact.email}</a>
                  </div>
                  <div className="flex items-center gap-2 text-[#6B7280]">
                    <Phone className="h-4 w-4" />
                    <a href={`tel:${t.contact.phone}`} className="text-[#33AADD] hover:underline">{t.contact.phone}</a>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CookiePolicyPage;
